import React, { useEffect, useMemo, useRef, useState } from "react";

import * as d3 from "d3";

import GraphCenterHoverCard from "./GraphCenterHoverCard";
import { getMyContactRowsForAccount } from "./myRelationshipsDataAdapter";
import { getCenterNodeInitials } from "./relationshipGraphUtils";
import {
  clampLevel,
  getInitials,
  meddpiccCanonicalToMatrixKey,
} from "../account-relationships/relationshipUtils";

const RL_EDGE_COLOR = {
  5: "#16a34a",
  4: "#22c55e",
  3: "#eab308",
  2: "#f59e0b",
  1: "#ef4444",
  0: "#ef4444",
};

const RL_EDGE_WIDTH = {
  5: 3.5,
  4: 3,
  3: 2.5,
  2: 2,
  1: 1.5,
  0: 1.5,
};

function meddScaleForKey(meddKey) {
  if (meddKey === "EB") return 1.15;
  if (meddKey === "CH") return 1.08;
  return 1;
}

function rlEdgeColor(rl) {
  if (rl === null || rl === undefined) return "#cbd5e1";
  return RL_EDGE_COLOR[clampLevel(rl)] || "#d1d5db";
}

function rlEdgeWidth(rl) {
  if (rl === null || rl === undefined) return 1;
  return RL_EDGE_WIDTH[clampLevel(rl)] || 1;
}

function buildCompareGraph(accounts, centerLabel = "You") {
  const nodes = [
    {
      id: "ego",
      type: "ego",
      label: centerLabel,
      initials: getCenterNodeInitials(centerLabel),
      accountId: null,
    },
  ];
  const edges = [];
  const nodeIds = new Set(["ego"]);

  (accounts || []).forEach(account => {
    const mappedRows = getMyContactRowsForAccount(account);
    const mappedByContactId = new Map(
      mappedRows.map(row => [String(row.id), row])
    );

    (account.contacts || []).forEach(contact => {
      const contactId = String(contact.id);
      const nodeId = `c-${contactId}-${account.id}`;
      if (nodeIds.has(nodeId)) return;
      nodeIds.add(nodeId);

      const mappedRow = mappedByContactId.get(contactId);
      const rl =
        mappedRow?.rlLevel !== undefined && mappedRow?.rlLevel !== null
          ? clampLevel(mappedRow.rlLevel)
          : null;
      const meddKey = meddpiccCanonicalToMatrixKey(
        contact.meddpiccRoleCanonical ||
          contact.raw?.meddpicc_role ||
          contact.meddpiccRole
      );

      nodes.push({
        id: nodeId,
        type: "customer",
        accountId: account.id,
        contactId,
        label: contact.name || "Contact",
        firstName: (contact.name || "Contact").split(" ")[0],
        initials: getInitials(contact.name),
        rl,
        assessed: rl !== null,
        meddScale: meddScaleForKey(meddKey),
        sectorColor: account.sectorColor || "#9333ea",
      });

      if (mappedRow) {
        edges.push({
          id: `e-ego-${contactId}-${account.id}`,
          source: "ego",
          target: nodeId,
          accountId: account.id,
          rl,
          assessed: rl !== null,
          color: rlEdgeColor(rl),
          width: rlEdgeWidth(rl),
        });
      }
    });
  });

  return { nodes, edges };
}

function layoutCompareGraph(graph, width, height) {
  const accountIds = [
    ...new Set(
      graph.nodes.filter(n => n.type === "customer").map(n => n.accountId)
    ),
  ];
  const nSectors = Math.max(1, accountIds.length);
  const sectorAngle = (2 * Math.PI) / nSectors;
  const isLowN = nSectors <= 3;
  const cx = width / 2;
  const cy = height / 2;
  const baseR = Math.min(cx, cy) - (isLowN ? 85 : 65);
  const outerR = baseR + 30;
  const spreadCapRad = (65 * Math.PI) / 180;
  const egoSize = isLowN ? 52 : 44;
  const contactBaseSize = isLowN ? 33 : 28;

  const positions = { ego: { x: cx, y: cy, size: egoSize } };

  accountIds.forEach((accountId, si) => {
    const sectorCenter = si * sectorAngle - Math.PI / 2;
    const sectorNodes = graph.nodes.filter(
      n => n.type === "customer" && n.accountId === accountId
    );
    const actualSpread = Math.min(sectorAngle * 0.8, spreadCapRad);

    sectorNodes.forEach((node, ci) => {
      const rlVal = node.rl !== null ? node.rl : 0;
      const dist = baseR * (1 - rlVal / 6) * 0.75 + baseR * 0.25;
      const jitter = (ci % 2 === 0 ? 1 : -1) * (ci * 3);
      const spreadFactor =
        sectorNodes.length > 1
          ? (ci / (sectorNodes.length - 1) - 0.5) * actualSpread
          : 0;
      positions[node.id] = {
        x: cx + Math.cos(sectorCenter + spreadFactor) * (dist + jitter),
        y: cy + Math.sin(sectorCenter + spreadFactor) * (dist + jitter),
        size: contactBaseSize * (node.meddScale || 1),
      };
    });
  });

  return {
    cx,
    cy,
    baseR,
    outerR,
    sectorAngle,
    accountIds,
    isLowN,
    positions,
    edgeOpacity: isLowN ? 0.78 : 0.5,
  };
}

/**
 * L1 compare map — ego at centre, account pie wedges, contacts radially by RL.
 */
export default function MyRelationshipsL1CompareMap({
  accounts = [],
  focusedAccountId = null,
  onFocusAccount,
  centerLabel = "You",
  centerUserStakeholder = null,
}) {
  const hostRef = useRef(null);
  const overlayRef = useRef(null);
  const [egoHovered, setEgoHovered] = useState(false);

  const graph = useMemo(
    () => buildCompareGraph(accounts, centerLabel),
    [accounts, centerLabel]
  );

  useEffect(() => {
    const host = hostRef.current;
    const overlay = overlayRef.current;
    if (!host) return undefined;

    const render = () => {
      const width = host.clientWidth || 700;
      const height = Math.max(420, host.clientHeight || 460);
      const layout = layoutCompareGraph(graph, width, height);
      const {
        cx,
        cy,
        baseR,
        outerR,
        sectorAngle,
        accountIds,
        isLowN,
        positions,
        edgeOpacity,
      } = layout;

      d3.select(host).selectAll("svg").remove();
      if (overlay) d3.select(overlay).selectAll("*").remove();

      const svg = d3
        .select(host)
        .append("svg")
        .attr("width", width)
        .attr("height", height)
        .attr("class", "t3-l1-svg")
        .style("position", "absolute")
        .style("inset", "0");

      const wedgeG = svg.append("g").attr("class", "t3-l1-wedges");
      const edgeG = svg.append("g").attr("class", "t3-l1-edges");
      const nodeG = svg.append("g").attr("class", "t3-l1-nodes");

      const accountById = new Map((accounts || []).map(a => [a.id, a]));
      const isFocused = accountId =>
        focusedAccountId && focusedAccountId === accountId;
      const sectorDim = accountId => focusedAccountId && !isFocused(accountId);

      accountIds.forEach((accountId, si) => {
        const startAngle = si * sectorAngle - Math.PI / 2 - sectorAngle / 2;
        const endAngle = startAngle + sectorAngle;
        const x1 = cx + Math.cos(startAngle) * outerR;
        const y1 = cy + Math.sin(startAngle) * outerR;
        const x2 = cx + Math.cos(endAngle) * outerR;
        const y2 = cy + Math.sin(endAngle) * outerR;
        const largeArc = sectorAngle > Math.PI ? 1 : 0;
        const color = accountById.get(accountId)?.sectorColor || "#9333ea";
        const wedgeOpacity = isFocused(accountId)
          ? 0.12
          : focusedAccountId
            ? 0.02
            : 0.06;

        wedgeG
          .append("path")
          .attr(
            "d",
            `M${cx},${cy} L${x1},${y1} A${outerR},${outerR} 0 ${largeArc},1 ${x2},${y2} Z`
          )
          .attr("fill", color)
          .attr("opacity", wedgeOpacity)
          .attr("class", "t3-l1-wedge")
          .style("cursor", "pointer")
          .style("pointer-events", "all")
          .on("click", () => {
            onFocusAccount?.(isFocused(accountId) ? null : accountId);
          });

        wedgeG
          .append("line")
          .attr("x1", cx)
          .attr("y1", cy)
          .attr("x2", x1)
          .attr("y2", y1)
          .attr("stroke", color)
          .attr("stroke-width", 0.5)
          .attr(
            "opacity",
            focusedAccountId ? (isFocused(accountId) ? 0.25 : 0.08) : 0.15
          )
          .style("pointer-events", "none");
      });

      graph.edges.forEach(edge => {
        const source = positions[edge.source];
        const target = positions[edge.target];
        if (!source || !target) return;
        const dimmed = sectorDim(edge.accountId);
        edgeG
          .append("line")
          .attr("x1", source.x)
          .attr("y1", source.y)
          .attr("x2", target.x)
          .attr("y2", target.y)
          .attr("stroke", edge.color)
          .attr("stroke-width", edge.width)
          .attr("opacity", dimmed ? 0.08 : edgeOpacity)
          .attr("stroke-dasharray", edge.assessed ? null : "5,4")
          .style("pointer-events", "none");
      });

      const ego = positions.ego;
      const egoNode = graph.nodes.find(n => n.id === "ego");
      const egoGrp = nodeG.append("g").attr("class", "t3-l1-ego");
      egoGrp
        .append("circle")
        .attr("cx", ego.x)
        .attr("cy", ego.y)
        .attr("r", ego.size / 2)
        .attr("fill", "#1e293b")
        .attr("stroke", "#fff")
        .attr("stroke-width", 2)
        .style("cursor", "pointer")
        .on("click", () => onFocusAccount?.(null))
        .on("mouseenter", () => setEgoHovered(true))
        .on("mouseleave", () => setEgoHovered(false));
      egoGrp
        .append("text")
        .attr("x", ego.x)
        .attr("y", ego.y + 4)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")
        .attr("font-size", isLowN ? 12 : 11)
        .attr("font-weight", 700)
        .attr("fill", "#fff")
        .style("pointer-events", "none")
        .text(egoNode?.initials || getCenterNodeInitials(centerLabel));

      graph.nodes
        .filter(n => n.type === "customer")
        .forEach(node => {
          const pos = positions[node.id];
          if (!pos) return;
          const dimmed = sectorDim(node.accountId);
          const focused = isFocused(node.accountId);
          const grp = nodeG
            .append("g")
            .attr("class", "t3-l1-contact")
            .style("cursor", "pointer")
            .attr("opacity", dimmed ? 0.2 : 1)
            .on("click", event => {
              event.stopPropagation();
              onFocusAccount?.(focused ? null : node.accountId);
            });

          grp
            .append("circle")
            .attr("cx", pos.x)
            .attr("cy", pos.y)
            .attr("r", pos.size / 2)
            .attr("fill", node.sectorColor)
            .attr("stroke", focused ? "#1e293b" : "#fff")
            .attr("stroke-width", focused ? 3 : 2);

          grp
            .append("text")
            .attr("x", pos.x)
            .attr("y", pos.y)
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .attr("font-size", 9)
            .attr("font-weight", 700)
            .attr("fill", "#fff")
            .text(node.initials);

          grp
            .append("text")
            .attr("x", pos.x)
            .attr("y", pos.y + pos.size / 2 + (isLowN ? 8 : 5))
            .attr("text-anchor", "middle")
            .attr("font-size", isLowN ? 10 : 9)
            .attr("font-weight", 600)
            .attr("fill", node.sectorColor)
            .text(node.firstName);

          grp
            .append("title")
            .text(
              `${node.label}${
                node.rl !== null ? ` · RL ${node.rl}` : " · RL ? · Unassessed"
              }`
            );
        });

      svg.on("click", () => onFocusAccount?.(null));

      if (overlay) {
        accountIds.forEach((accountId, si) => {
          const angle = si * sectorAngle - Math.PI / 2;
          const lx = cx + Math.cos(angle) * (baseR + 48);
          const ly = cy + Math.sin(angle) * (baseR + 48);
          const account = accountById.get(accountId);
          const color = account?.sectorColor || "#9333ea";
          d3.select(overlay)
            .append("button")
            .attr("type", "button")
            .attr("class", "t3-l1-sector-lbl")
            .style("position", "absolute")
            .style("left", `${lx}px`)
            .style("top", `${ly}px`)
            .style("transform", "translate(-50%, -50%)")
            .style("font-size", "11px")
            .style("font-weight", "700")
            .style("color", color)
            .style("text-transform", "uppercase")
            .style("letter-spacing", "0.05em")
            .style("background", "transparent")
            .style("border", "none")
            .style("cursor", "pointer")
            .style("padding", "2px 6px")
            .style("text-shadow", "0 0 4px #fff, 0 0 4px #fff")
            .style(
              "opacity",
              focusedAccountId ? (isFocused(accountId) ? 1 : 0.25) : 1
            )
            .style("pointer-events", "auto")
            .text(account?.name || accountId)
            .on("click", () => {
              onFocusAccount?.(isFocused(accountId) ? null : accountId);
            });
        });
      }
    };

    render();
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(render) : null;
    observer?.observe(host);
    return () => {
      observer?.disconnect();
      d3.select(host).selectAll("*").remove();
      if (overlay) d3.select(overlay).selectAll("*").remove();
    };
  }, [graph, accounts, focusedAccountId, onFocusAccount, centerLabel]);

  if (!accounts.length) {
    return (
      <div
        className="t3-map-container"
        style={{
          minHeight: 440,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div className="t3-l2-empty">
          <b>Select accounts to compare</b>
          <span>Pick at least two accounts, then click Compare.</span>
        </div>
      </div>
    );
  }

  return (
    <div
      id="t3-map-container"
      className="t3-map-container"
      style={{
        width: "100%",
        height: "100%",
        minHeight: 440,
        position: "relative",
      }}
    >
      <div
        ref={hostRef}
        style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 34 }}
      />
      <div
        ref={overlayRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 34,
          zIndex: 1,
          pointerEvents: "none",
        }}
      />
      <GraphCenterHoverCard
        visible={egoHovered}
        centerUserStakeholder={centerUserStakeholder}
        nameFallback={centerLabel}
      />
      <div
        className="t3-l0-legend"
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 34,
          background: "rgba(255,255,255,0.92)",
          borderTop: "1px solid var(--border)",
          zIndex: 1,
        }}
      >
        <span className="lg-item">
          <b>{centerLabel}</b> at centre · contacts by RL distance
        </span>
        <span className="lg-item">
          <b>Click wedge / card</b> to focus an account sector
        </span>
        <span className="lg-item">dashed = mapped · not assessed</span>
      </div>
    </div>
  );
}
